import { useState, useCallback } from 'react';
import { userFeedbackService } from '../services/userFeedbackService';
import { FeedbackData, FeedbackResponse, UserFeedback } from '../types/feedback';

interface UseUserFeedbackReturn {
  // App feedback state
  appFeedback: UserFeedback | null;
  appFeedbackLoading: boolean;
  appFeedbackError: string | null;
  
  // Session feedback state
  sessionFeedback: UserFeedback | null;
  sessionFeedbackLoading: boolean;
  sessionFeedbackError: string | null;
  
  // Actions
  loadAppFeedback: () => Promise<void>;
  submitAppFeedback: (feedbackData: FeedbackData) => Promise<boolean>;
  loadSessionFeedback: (sessionId: string) => Promise<void>;
  submitSessionFeedback: (sessionId: string, feedbackData: FeedbackData) => Promise<boolean>;
  clearSessionFeedback: () => void;
  clearAppFeedback: () => void;
  clearErrors: () => void;
  
  // Helper state
  isLoading: boolean;
  hasAnyError: boolean;
}

export const useUserFeedback = (): UseUserFeedbackReturn => {
  // App feedback state
  const [appFeedback, setAppFeedback] = useState<UserFeedback | null>(null);
  const [appFeedbackLoading, setAppFeedbackLoading] = useState(false);
  const [appFeedbackError, setAppFeedbackError] = useState<string | null>(null);
  
  // Session feedback state
  const [sessionFeedback, setSessionFeedback] = useState<UserFeedback | null>(null);
  const [sessionFeedbackLoading, setSessionFeedbackLoading] = useState(false);
  const [sessionFeedbackError, setSessionFeedbackError] = useState<string | null>(null);
  
  // Load app feedback
  const loadAppFeedback = useCallback(async () => {
    setAppFeedbackLoading(true);
    setAppFeedbackError(null);
    
    try {
      const response: FeedbackResponse = await userFeedbackService.getAppFeedback();
      
      // Handle different response structures
      if (response && (response.success === true || response.success === undefined)) {
        // If response has feedback property, use it; otherwise treat the response itself as feedback
        const feedback = response.feedback || response;
        
        if (feedback && (feedback.feedbackData || feedback.id)) {
          setAppFeedback(feedback);
        } else {
          setAppFeedback(null);
        }
      } else {
        setAppFeedback(null);
      }
    } catch (error) {
      // Don't set error state for 404s - just use empty values
      setAppFeedback(null);
    } finally {
      setAppFeedbackLoading(false);
    }
  }, []);
  
  // Submit app feedback
  const submitAppFeedback = useCallback(async (feedbackData: FeedbackData): Promise<boolean> => {
    setAppFeedbackLoading(true);
    setAppFeedbackError(null);
    
    try {
      const response: FeedbackResponse = await userFeedbackService.submitAppFeedback(feedbackData);
      
      // Handle different response structures
      if (response && (response.success === true || response.success === undefined)) {
        // If response has feedback property, use it; otherwise treat the response itself as feedback
        const feedback = response.feedback || response;
        if (feedback) {
          setAppFeedback(feedback);
          return true;
        }
      }
      
      console.error('useUserFeedback: App feedback submission failed - invalid response structure');
      setAppFeedbackError('Failed to submit app feedback');
      return false;
    } catch (error) {
      console.error('useUserFeedback: Failed to submit app feedback:', error);
      setAppFeedbackError(error instanceof Error ? error.message : 'Failed to submit app feedback');
      return false;
    } finally {
      setAppFeedbackLoading(false);
    }
  }, []);
  
  // Load session feedback
  const loadSessionFeedback = useCallback(async (sessionId: string) => {
    if (!sessionId) {
      return;
    }
    
    setSessionFeedbackLoading(true);
    setSessionFeedbackError(null);
    
    try {
      const response: FeedbackResponse = await userFeedbackService.getSessionFeedback(sessionId);
      
      let feedback = null;
      
      // Handle different response structures
      if (response) {
        if (Array.isArray(response) && response.length > 0) {
          // If response is an array, take the first element
          feedback = response[0];
        } else if (response.success === true || response.success === undefined) {
          // Standard object response
          feedback = response.feedback || response;
        }
        
        if (feedback && (feedback.feedbackData || feedback.id || feedback.type)) {
          setSessionFeedback(feedback);
        } else {
          setSessionFeedback(null);
        }
      } else {
        setSessionFeedback(null);
      }
    } catch (error) {
      // Don't set error state for 404s - just use empty values
      setSessionFeedback(null);
    } finally {
      setSessionFeedbackLoading(false);
    }
  }, []);
  
  // Submit session feedback
  const submitSessionFeedback = useCallback(async (sessionId: string, feedbackData: FeedbackData): Promise<boolean> => {
    if (!sessionId) return false;
    
    setSessionFeedbackLoading(true);
    setSessionFeedbackError(null);
    
    try {
      const response: FeedbackResponse = await userFeedbackService.submitSessionFeedback(sessionId, feedbackData);
      
      // Handle different response structures
      if (response && (response.success === true || response.success === undefined)) {
        // If response has feedback property, use it; otherwise treat the response itself as feedback
        const feedback = response.feedback || response;
        if (feedback) {
          setSessionFeedback(feedback);
          return true;
        }
      }
      
      console.error('useUserFeedback: Session feedback submission failed - invalid response structure');
      setSessionFeedbackError('Failed to submit session feedback');
      return false;
    } catch (error) {
      console.error('useUserFeedback: Failed to submit session feedback:', error);
      setSessionFeedbackError(error instanceof Error ? error.message : 'Failed to submit session feedback');
      return false;
    } finally {
      setSessionFeedbackLoading(false);
    }
  }, []);
  
  // Clear all errors
  const clearErrors = useCallback(() => {
    setAppFeedbackError(null);
    setSessionFeedbackError(null);
  }, []);
  
  // Clear session feedback (useful when switching sessions)
  const clearSessionFeedback = useCallback(() => {
    setSessionFeedback(null);
    setSessionFeedbackError(null);
  }, []);
  
  // Clear app feedback (useful after successful submission)
  const clearAppFeedback = useCallback(() => {
    setAppFeedback(null);
    setAppFeedbackError(null);
  }, []);
  
  // Helper computed values
  const isLoading = appFeedbackLoading || sessionFeedbackLoading;
  const hasAnyError = !!(appFeedbackError || sessionFeedbackError);
  
  return {
    // App feedback state
    appFeedback,
    appFeedbackLoading,
    appFeedbackError,
    
    // Session feedback state
    sessionFeedback,
    sessionFeedbackLoading,
    sessionFeedbackError,
    
    // Actions
    loadAppFeedback,
    submitAppFeedback,
    loadSessionFeedback,
    submitSessionFeedback,
    clearSessionFeedback,
    clearAppFeedback,
    clearErrors,
    
    // Helper state
    isLoading,
    hasAnyError
  };
};
